import type { WorkSchedule, WorkScheduleWeek } from '@trinnvis/dabih-api-client';
import { numberWord } from 'src/utilities/text';
import { t } from 'src/i18n';

export function workHoursDescription(schedule: WorkSchedule) {
  let result = '';
  if (schedule.workWeeks.length > 1) {
    result = t('employees.workHours.varyingCycle', { count: numberWord(schedule.workWeeks.length) });
  } else {
    const week = schedule.workWeeks[0];
    let defaultWorkHours: string;
    if (!schedule.workHoursDefinedPerDay) {
      defaultWorkHours = schedule.workDayStartTime + '-' + schedule.workDayEndTime;
      if (
        week.monday.workDay &&
        week.tuesday.workDay &&
        week.wednesday.workDay &&
        week.thursday.workDay &&
        week.friday.workDay &&
        !week.saturday.workDay &&
        !week.sunday.workDay
      ) {
        return t('employees.workHours.mondayToFridayHours', { hours: defaultWorkHours });
      } else {
        result += week.monday.workDay ? t('employees.workHours.mondayShort') : '';
        result += week.tuesday.workDay ? t('employees.workHours.tuesdayShort') : '';
        result += week.wednesday.workDay ? t('employees.workHours.wednesdayShort') : '';
        result += week.thursday.workDay ? t('employees.workHours.thursdayShort') : '';
        result += week.friday.workDay ? t('employees.workHours.fridayShort') : '';
        result += week.saturday.workDay ? t('employees.workHours.saturdayShort') : '';
        result += week.sunday.workDay ? t('employees.workHours.sundayShort') : '';
        result += defaultWorkHours;
      }
    } else {
      result = weekScheduleDescription(week);
    }
  }
  return result;
}
export function weekScheduleDescription(week: WorkScheduleWeek): string {
  const dayNames: Record<string, string> = {
    monday: t('employees.workHours.monday'),
    tuesday: t('employees.workHours.tuesday'),
    wednesday: t('employees.workHours.wednesday'),
    thursday: t('employees.workHours.thursday'),
    friday: t('employees.workHours.friday'),
    saturday: t('employees.workHours.saturday'),
    sunday: t('employees.workHours.sunday'),
  };
  const daysOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
  // Group consecutive days with same time
  const groups: Array<{ days: string[]; start: string; end: string }> = [];
  let currentGroup: { days: string[]; start: string; end: string } | null = null;
  for (const day of daysOrder) {
    const dayData = week[day];
    if (dayData && dayData.workDay && dayData.start && dayData.end) {
      if (currentGroup && currentGroup.start === dayData.start && currentGroup.end === dayData.end) {
        // Same time as current group, add to it
        currentGroup.days.push(day);
      } else {
        // Different time or first group
        if (currentGroup) {
          groups.push(currentGroup);
        }
        currentGroup = {
          days: [day],
          start: dayData.start,
          end: dayData.end,
        };
      }
    } else {
      // Not a work day, break current group
      if (currentGroup) {
        groups.push(currentGroup);
        currentGroup = null;
      }
    }
  }
  // Add the last group if any
  if (currentGroup) {
    groups.push(currentGroup);
  }
  if (groups.length === 0) {
    return t('employees.workHours.none');
  }
  // Check if all 7 days have the same time
  if (groups.length === 1 && groups[0].days.length === 7) {
    return t('employees.workHours.allDaysAt', { start: groups[0].start, end: groups[0].end });
  }
  // Format each group
  const formatted = groups.map((group) => {
    let dayRange: string;
    if (group.days.length === 1) {
      dayRange = dayNames[group.days[0]];
    } else {
      // Check if it's a standard Monday-Friday pattern
      const isMonToFri =
        group.days.length === 5 &&
        group.days[0] === 'monday' &&
        group.days[1] === 'tuesday' &&
        group.days[2] === 'wednesday' &&
        group.days[3] === 'thursday' &&
        group.days[4] === 'friday';
      if (isMonToFri) {
        dayRange = t('employees.workHours.mondayToFriday');
      } else {
        // Consecutive days, format as range
        dayRange = t('employees.workHours.dayRange', {
          from: dayNames[group.days[0]],
          to: dayNames[group.days[group.days.length - 1]],
        });
      }
    }
    return t('employees.workHours.groupAt', { dayRange, start: group.start, end: group.end });
  });
  return formatted.join(', ');
}
